import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';
import { ChevronDown, Search } from 'lucide-react';

export interface SearchSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  tag?: string;
  category?: string;
}

interface SearchSelectProps {
  options: SearchSelectOption[] | Record<string, SearchSelectOption[]>;
  placeholder?: string;
  searchPlaceholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  disabled?: boolean;
  error?: string;
  label?: string;
  required?: boolean;
  size?: 'sm' | 'md';
}

const SearchSelect: React.FC<SearchSelectProps> = ({
  options,
  placeholder = 'Select an option',
  searchPlaceholder = 'Find or specify an attribute...',
  value,
  onChange,
  className,
  disabled = false,
  error,
  label,
  required = false,
  size = 'md',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [filteredOptions, setFilteredOptions] = useState<SearchSelectOption[]>(
    []
  );
  const [groupedOptions, setGroupedOptions] = useState<
    Record<string, SearchSelectOption[]>
  >({});

  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [dropdownRect, setDropdownRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  // Flatten options for easier searching
  const flattenOptions = (
    opts: SearchSelectOption[] | Record<string, SearchSelectOption[]>
  ): SearchSelectOption[] => {
    if (Array.isArray(opts)) {
      return opts;
    }
    return Object.values(opts).flat();
  };

  // Get selected option label
  const getSelectedLabel = (): string => {
    const allOptions = flattenOptions(options);
    const selected = allOptions.find((opt) => opt.value === value);
    return selected?.label || placeholder;
  };

  // Filter options based on search term
  useEffect(() => {
    const allOptions = flattenOptions(options);
    const filtered = allOptions.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);

    // Group filtered options by category if they have categories
    const grouped: Record<string, SearchSelectOption[]> = {};
    filtered.forEach((option) => {
      const category = option.category || 'Values';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(option);
    });
    setGroupedOptions(grouped);
  }, [searchTerm, options]);

  // Compute dropdown position when open
  const updateDropdownRect = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownRect({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setDropdownRect(null);
      return;
    }
    updateDropdownRect();
    const handleScrollOrResize = () => updateDropdownRect();
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen]);

  // Handle click outside to close dropdown (trigger + portaled content)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = triggerRef.current?.contains(target);
      const inDropdown = dropdownContentRef.current?.contains(target);
      if (!inTrigger && !inDropdown) {
        setIsOpen(false);
        setSearchTerm('');
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredOptions.length) {
          handleOptionSelect(filteredOptions[selectedIndex].value);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setSelectedIndex(-1);
        break;
    }
  };

  // Scroll selected option into view
  useEffect(() => {
    if (selectedIndex >= 0 && optionRefs.current[selectedIndex]) {
      optionRefs.current[selectedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selectedIndex]);

  const handleOptionSelect = (optionValue: string) => {
    onChange?.(optionValue);
    setIsOpen(false);
    setSearchTerm('');
    setSelectedIndex(-1);
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
        setSelectedIndex(-1);
      }
    }
  };

  return (
    <div className={cn('relative min-w-0', className)}>
      {label && (
        <label
          className={cn(
            'block font-medium text-gray-700',
            size === 'sm' ? 'text-xs mb-1' : 'text-sm mb-2'
          )}
        >
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}

      <div className="relative min-w-0" ref={triggerRef}>
        {/* Trigger Button */}
        <button
          type="button"
          className={cn(
            'w-full min-w-0 flex items-center justify-between gap-2 border border-border shadow-sm bg-white text-left focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors',
            size === 'sm'
              ? 'px-2 py-1 rounded-md text-sm'
              : 'px-3 py-2 rounded-lg',
            error && 'border-red-500 focus:ring-red-500 focus:border-red-500',
            disabled && 'bg-gray-50 cursor-not-allowed text-gray-500',
            !disabled && 'hover:border-gray-400'
          )}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className={cn('truncate min-w-0', !value && 'text-gray-500')}>
            {getSelectedLabel()}
          </span>
          <ChevronDown
            className={cn(
              'flex-shrink-0 transition-transform',
              size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4',
              isOpen && 'rotate-180'
            )}
          />
        </button>

        {/* Dropdown via portal */}
        {isOpen &&
          dropdownRect &&
          createPortal(
            <div
              ref={dropdownContentRef}
              className="z-50 min-w-[250px] bg-white border border-border rounded-lg shadow-lg overflow-hidden"
              style={{
                position: 'fixed',
                top: dropdownRect.top + 4,
                left: dropdownRect.left,
                width: Math.max(dropdownRect.width, 250),
              }}
            >
              {/* Search Input */}
              <div className="p-3 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>

              {/* Options List */}
              <div className="max-h-48 overflow-y-auto">
                {Object.keys(groupedOptions).length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No options found
                  </div>
                ) : (
                  Object.entries(groupedOptions).map(
                    ([category, categoryOptions]) => (
                      <div key={category}>
                        {/* Category Header */}
                        <div className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wide bg-gray-50">
                          {category}
                        </div>

                        {/* Category Options */}
                        {categoryOptions.map((option) => {
                          const globalIndex = filteredOptions.findIndex(
                            (opt) => opt.value === option.value
                          );
                          return (
                            <div
                              key={option.value}
                              ref={(el) => {
                                optionRefs.current[globalIndex] = el;
                              }}
                              className={cn(
                                'px-3 py-2 text-sm cursor-pointer flex items-center justify-between hover:bg-gray-100',
                                globalIndex === selectedIndex &&
                                  'bg-primary-background',
                                option.disabled &&
                                  'opacity-50 cursor-not-allowed',
                                option.value === value &&
                                  'bg-primary-muted text-primary'
                              )}
                              onClick={() =>
                                !option.disabled &&
                                handleOptionSelect(option.value)
                              }
                            >
                              <span className="truncate">{option.label}</span>
                              {option.tag && (
                                <span className="ml-2 px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">
                                  {option.tag}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )
                  )
                )}
              </div>
            </div>,
            document.body
          )}
      </div>

      {error && (
        <p
          className={cn(
            'mt-1 text-destructive',
            size === 'sm' ? 'text-xs' : 'text-sm'
          )}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default SearchSelect;
